import type { FC } from 'react';

import { GlobalOutlined } from '@ant-design/icons';
import { Button, Divider, Input, Popover, Space, Tag, Tooltip, Typography, message } from 'antd';
import React, { useState } from 'react';
import { EProjectNames, PROJECT_DATA } from '../../../Hooks/useProjectData';
import { MULTI_PLATFORMS, PingStatus, usePlatformSessions } from '../../../Hooks/usePlatformSessions';

const { Text } = Typography;

const StatusDot: FC<{ status?: PingStatus }> = ({ status }) => (
  <span
    style={{
      display: 'inline-block',
      width: 8,
      height: 8,
      borderRadius: '50%',
      marginRight: 8,
      flexShrink: 0,
      background: status === 'online' ? '#52c41a' : status === 'offline' ? '#ff4d4f' : '#d9d9d9',
    }}
  />
);

interface PlatformRowProps {
  code: EProjectNames;
  status?: PingStatus;
  isSelected: boolean;
  onSwitch: (code: EProjectNames) => void;
  onLogin: (code: EProjectNames, email: string, password: string) => Promise<void>;
}

const PlatformRow: FC<PlatformRowProps> = ({ code, status, isSelected, onSwitch, onLogin }) => {
  const [loggingIn, setLoggingIn] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleLogin = async () => {
    if (!email || !password) return;
    setSubmitting(true);
    try {
      await onLogin(code, email, password);
      message.success(`${PROJECT_DATA[code].title} connected`);
      setLoggingIn(false);
      setEmail('');
      setPassword('');
    } catch {
      message.error(`Could not log in to ${PROJECT_DATA[code].title}`);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={{ padding: '6px 0' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
        <span style={{ display: 'flex', alignItems: 'center', minWidth: 0 }}>
          <StatusDot status={status} />
          <Text strong style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {PROJECT_DATA[code].title}
          </Text>
        </span>

        {status === 'checking' ? (
          <Text type="secondary" style={{ fontSize: 12 }}>
            Checking...
          </Text>
        ) : status === 'online' ? (
          isSelected ? (
            <Tag color="blue">Current</Tag>
          ) : (
            <Button size="small" onClick={() => onSwitch(code)}>
              Switch
            </Button>
          )
        ) : (
          <Button size="small" onClick={() => setLoggingIn((v) => !v)}>
            {loggingIn ? 'Cancel' : 'Log in'}
          </Button>
        )}
      </div>

      {loggingIn && (
        <Space direction="vertical" style={{ width: '100%', marginTop: 8 }} size={6}>
          <Input
            placeholder="Email"
            size="small"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
          <Input.Password
            placeholder="Password"
            size="small"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            onPressEnter={handleLogin}
          />
          <Button size="small" type="primary" block loading={submitting} onClick={handleLogin}>
            Connect
          </Button>
        </Space>
      )}
    </div>
  );
};

const PlatformSwitcher: FC<{ color?: string }> = ({ color }) => {
  const { statuses, selected, loginToPlatform, switchToPlatform, pingAll } = usePlatformSessions();
  const [open, setOpen] = useState(false);

  const content = (
    <div style={{ width: 260 }}>
      {MULTI_PLATFORMS.map((code, index) => (
        <React.Fragment key={code}>
          {index > 0 && <Divider style={{ margin: '2px 0' }} />}
          <PlatformRow
            code={code}
            status={statuses[code]}
            isSelected={selected === code}
            onSwitch={switchToPlatform}
            onLogin={loginToPlatform}
          />
        </React.Fragment>
      ))}
    </div>
  );

  return (
    <Popover
      content={content}
      title="Platforms"
      trigger="click"
      open={open}
      onOpenChange={(v) => {
        setOpen(v);
        if (v) pingAll();
      }}
      placement="bottomRight"
    >
      <Tooltip title="Switch platform">
        <span style={{ cursor: 'pointer', color }}>
          <GlobalOutlined rev={undefined} style={{ fontSize: 18 }} />
        </span>
      </Tooltip>
    </Popover>
  );
};

export default PlatformSwitcher;
